import { useEffect, useRef, useState, type ReactNode } from "react";
import { MediaImage } from "../../components/MediaImage";
import { resolve, type MediaVariant } from "./mediaCache";
import type { Attachment } from "../../api/types";

/** Two tries, then the fallback. A picture is not worth hammering the API for. */
const MAX_ATTEMPTS = 2;
const RETRY_DELAY_MS = 800;

interface UseCachedMediaOptions {
  media: Attachment | null;
  /** Thumbnails are what lists and previews want; the hero wants the big copy. */
  prefer: "thumbnail" | "display";
  /** Asks the caller to refetch the record, in case the signature really lapsed. */
  onUnavailable?: () => void;
}

/**
 * The bytes of one attachment, as a url this device already holds.
 *
 * It reads out of `mediaCache`, so a file is downloaded once and then survives
 * navigation and reload — and it owns what happens when that fails.
 *
 * The failure half is the reason this exists. The screens used to remember which
 * url had failed and refuse to try it again, on the reasoning that a refetch
 * would hand back a new signature and therefore a new url worth attempting.
 * Download tokens are bucketed now (apps/api/files/signing.py), so a refetch
 * inside the same window returns the *identical* url — the guard could never
 * clear, and one dropped connection left a permanent hole that only a page
 * reload could fill. So: retry explicitly, on our own terms, and treat a lapsed
 * signature as one of the things a retry is for rather than the only one.
 *
 * Returned as a hook and not only as a component because a video's `poster` is
 * an attribute, not an element, and it deserves the same cached still the list
 * already downloaded.
 */
export function useCachedMedia({ media, prefer, onUnavailable }: UseCachedMediaOptions): {
  src: string | null;
  broken: boolean;
} {
  // Take the smallest copy that is at least as big as what was asked for, and
  // walk up from there — a file uploaded before a derivative existed, or one the
  // browser could not decode, simply has fewer rungs.
  //
  // The original is the last rung and only an image may use it: a video streams
  // in ranges and a document is not a picture at all, so either would be pulled
  // down whole and handed to an <img> for nothing. Those get the caller's glyph.
  const isImage = media?.mime_type.startsWith("image/") ?? false;
  // Walk up: list wants the smallest usable still; the hero wants display then
  // the archival master. Only images may request "original" as an <img> source.
  const ladder: MediaVariant[] =
    prefer === "thumbnail"
      ? isImage
        ? ["thumbnail", "display", "original"]
        : ["thumbnail", "display"]
      : isImage
        ? ["display", "original", "thumbnail"]
        : ["display", "thumbnail"];
  const rungs = ladder
    .map((name) => [name, urlOf(media, name)] as const)
    .filter((entry): entry is [MediaVariant, string] => Boolean(entry[1]));

  const attachmentId = media?.id ?? null;
  const [tracked, setTracked] = useState(attachmentId);
  const [rungIndex, setRungIndex] = useState(0);
  const [attempt, setAttempt] = useState(0);
  // `attempt` only ever climbs, so that a retry always lands on a url the
  // browser has not already judged. The budget is what says when to stop.
  const [budget, setBudget] = useState(MAX_ATTEMPTS);
  const [src, setSrc] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);

  // A different attachment is a different picture, and starts over with a full
  // set of attempts.
  if (tracked !== attachmentId) {
    setTracked(attachmentId);
    setRungIndex(0);
    setAttempt(0);
    setBudget(MAX_ATTEMPTS);
    setSrc(null);
    setBroken(false);
  }

  const safeRung = rungs.length === 0 ? 0 : Math.min(rungIndex, rungs.length - 1);
  const [variant, sourceUrl]: readonly [MediaVariant, string | null] = rungs[safeRung] ?? [
    "original",
    null,
  ];
  const mediaId = sourceUrl ? attachmentId : null;

  // Read through refs, so a background refetch handing back an equal-but-new
  // record object does not abort a download that is already in flight — and so
  // that a retry picks up whatever url the refetch did deliver.
  const latest = useRef({
    url: sourceUrl,
    notify: onUnavailable,
    rungCount: rungs.length,
    rungIndex: safeRung,
  });
  latest.current = {
    url: sourceUrl,
    notify: onUnavailable,
    rungCount: rungs.length,
    rungIndex: safeRung,
  };

  useEffect(() => {
    const url = latest.current.url;
    if (!mediaId || !url) return;

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let objectUrl: string | null = null;
    let live = true;

    // A retry needs a url the browser has not already made up its mind about.
    // Only `token` is signed, so an extra parameter costs nothing.
    resolve(mediaId, variant, attempt === 0 ? url : `${url}&r=${attempt}`, controller.signal)
      .then((resolved) => {
        if (!live) {
          URL.revokeObjectURL(resolved);
          return;
        }
        objectUrl = resolved;
        setSrc(resolved);
      })
      .catch(() => {
        if (!live) return;
        if (attempt + 1 >= budget) {
          // This rung is spent — try a larger copy before giving up entirely.
          if (latest.current.rungIndex + 1 < latest.current.rungCount) {
            setRungIndex((current) => current + 1);
            setAttempt(0);
            setBudget(MAX_ATTEMPTS);
            setSrc(null);
            return;
          }
          setBroken(true);
          return;
        }
        // The record may be holding a url whose token has lapsed; ask for a
        // fresh one in parallel with trying again. `useMediaRefresh` throttles
        // this, so a whole list failing at once is still one refetch.
        latest.current.notify?.();
        timer = setTimeout(() => setAttempt((current) => current + 1), RETRY_DELAY_MS);
      });

    return () => {
      live = false;
      controller.abort();
      clearTimeout(timer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId, variant, attempt, budget]);

  // Giving up has to be temporary. A picture that ran out of tries should get
  // more of them the moment the thing that broke it plausibly changed — the
  // connection came back, or the app was brought to the front again. Without
  // this the only way out of an empty frame is still a page reload, which is
  // the whole complaint this hook exists to answer.
  useEffect(() => {
    if (!broken) return;
    const rearm = () => {
      if (document.visibilityState === "hidden") return;
      setBroken(false);
      setAttempt((current) => current + 1);
      setBudget((current) => current + MAX_ATTEMPTS);
    };
    window.addEventListener("online", rearm);
    document.addEventListener("visibilitychange", rearm);
    return () => {
      window.removeEventListener("online", rearm);
      document.removeEventListener("visibilitychange", rearm);
    };
  }, [broken]);

  return { src, broken };
}

interface CachedMediaProps extends UseCachedMediaOptions {
  /** Drawn while nothing has loaded yet. */
  fallback: ReactNode;
  /** Drawn instead once the retries are spent. */
  brokenFallback?: ReactNode;
  className?: string;
  alt?: string;
  eager?: boolean;
  /** Passed through to `MediaImage`: this layer supplies its own arrival. */
  plain?: boolean;
}

/** Every picture the app draws from the server comes through here. */
export function CachedMedia({
  media,
  prefer,
  onUnavailable,
  fallback,
  brokenFallback,
  className,
  alt = "",
  eager,
  plain,
}: CachedMediaProps) {
  const { src, broken } = useCachedMedia({ media, prefer, onUnavailable });
  // A blob that will not decode — a truncated cache entry, or a format this
  // WebView cannot read. Retrying the network would not help.
  const [undecodable, setUndecodable] = useState<string | null>(null);

  if (broken || (src !== null && src === undecodable)) return <>{brokenFallback ?? fallback}</>;
  if (!src) return <>{fallback}</>;

  return (
    <MediaImage
      src={src}
      alt={alt}
      className={className}
      eager={eager}
      plain={plain}
      onError={() => setUndecodable(src)}
    />
  );
}

function urlOf(media: Attachment | null, variant: MediaVariant): string | null {
  if (!media) return null;
  if (variant === "thumbnail") return media.thumbnail_url;
  if (variant === "display") return media.display_url;
  return media.url;
}
